'use client';

import React from 'react';
import useSWR from 'swr';
import { useSitePreferences } from 'context/site-preferences';
import { STATE_ABBREVIATIONS } from 'helpers/location/constant';
import { classnames } from 'helpers/utils/classnames';
import { useLocationStore } from 'stores/location';
import Cell from './components/cell';
import Grid from './components/grid';
import SkipToContent from './components/skip-to-content';
import TasticWrapper from './components/tastic-wrapper';
import { RendererProps } from './types';
import { highlight } from './utils/highlight';
import { locationFetcher } from './utils/location';

const Renderer = ({
  data: pageData,
  params,
  searchParams,
  currentHighlight,
  gridClassName = '',
  wrapperClassName = '',
  categories = [],
  flattenedCategories = [],
}: RendererProps) => {
  const { page, data } = pageData;

  const updateLocation = useLocationStore((state) => state.updateLocation);
  const locationDetails = useLocationStore((state) => state);
  const { defaultLocation = {}, googleMapKey } = useSitePreferences();
  let loc = {} as any;
  try {
    loc = JSON.parse(defaultLocation ?? {});
  } catch (error) {
    console.warn('Failed to parse defaultLocation:', error, defaultLocation);
    loc = {};
  }

  const sections = [page.sections.head, page.sections.main, page.sections.footer];

  const sectionsClassNames = {
    head: '',
    main: 'grow body-section',
    footer: '',
  } as Record<string, string>;
  const shouldFetch = locationDetails.isHydrated && (!locationDetails.lat || !locationDetails.lng);

  useSWR(
    shouldFetch ? ['user-location', loc?.defaultLat, loc?.defaultLng] : null,
    ([_, lat, lng]) => locationFetcher(lat, lng, googleMapKey),
    {
      revalidateOnFocus: false,
      onSuccess: (data) => {
        if (data?.lat) {
          updateLocation({
            lat: Number(data?.lat),
            lng: Number(data?.lng),
            state: STATE_ABBREVIATIONS[data?.state],
            countryFullName: data?.country,
            location: `${data?.cityName}, ${data?.stateCode}`,
          });
        }
      },
    },
  );

  return (
    <div className="flex min-h-screen flex-col items-stretch justify-start">
      <SkipToContent />
      {sections.filter(Boolean).map((section) => {
        const sectionContent = (
          <Grid
            key={section?.sectionId}
            gridClassName={gridClassName}
            wrapperClassName={classnames(
              'relative w-full',
              wrapperClassName,
              highlight(currentHighlight === section?.sectionId),
              sectionsClassNames[section?.sectionId ?? ''],
            )}
          >
            {section?.layoutElements?.map((layoutElement) => (
              <Cell
                key={layoutElement.layoutElementId}
                configuration={layoutElement.configuration}
                isHighlighted={currentHighlight === layoutElement.layoutElementId}
              >
                {layoutElement.tastics.map((tastic) => (
                  <TasticWrapper
                    key={tastic.tasticId}
                    data={tastic}
                    params={params}
                    searchParams={searchParams}
                    dataSources={data.dataSources}
                    isHighlighted={currentHighlight === tastic.tasticId}
                    categories={categories}
                    flattenedCategories={flattenedCategories}
                  />
                ))}
              </Cell>
            ))}
          </Grid>
        );

        // If it's the "main" section, wrap with <main>
        if (section?.sectionId === 'main') {
          return (
            <main key="main" id="main-content" tabIndex={-1}>
              {sectionContent}
            </main>
          );
        }

        return sectionContent;
      })}
    </div>
  );
};

export default Renderer;
